/**
 * Super-admin · Create Project.
 *
 * Onboard a project end-to-end: pick a tenant, set the GitHub repo + coding
 * config, and upload the four onboarding documents — BRD.md, architecture.md,
 * code-helper.md (context the Atlas coding agent reads), and tasks.xlsx
 * (serial_id + description rows, parsed into the project's backlog). Then assign
 * employees on the Assign Project tab — those people become the project's team,
 * and the daily Atlas scheduler works one backlog task per day, rotating through
 * them. Track progress on the Existing projects tab.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type CSSProperties, type FormEvent } from "react";

import { api, extractErrorMessage } from "@/lib/api";
import type { ProjectDocStatus, SuperProject, TasksImportResult } from "@/lib/types";

const MERGE_POLICIES: { value: string; label: string }[] = [
  { value: "always", label: "Auto-merge after PR" },
  { value: "tests_pass", label: "Merge only if tests pass" },
  { value: "human_approval", label: "Open PR only (human merges)" },
];

export function CreateProjectPage() {
  const qc = useQueryClient();
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [color, setColor] = useState("#7a3dff");
  const [githubRepo, setGithubRepo] = useState("");
  const [defaultBranch, setDefaultBranch] = useState("main");
  const [testCommand, setTestCommand] = useState("");
  const [mergePolicy, setMergePolicy] = useState("always");
  const [brd, setBrd] = useState<File | null>(null);
  const [architecture, setArchitecture] = useState<File | null>(null);
  const [codeHelper, setCodeHelper] = useState<File | null>(null);
  const [tasksFile, setTasksFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const projectsQuery = useQuery({
    queryKey: ["superadmin-projects"],
    queryFn: async () => (await api.get<SuperProject[]>("/superadmin/projects")).data,
  });
  const projects = projectsQuery.data ?? [];

  const create = useMutation({
    mutationFn: async () => {
      // 1) Create the project with its coding config.
      const project = (
        await api.post<SuperProject>("/superadmin/projects", {
          key: key.trim().toUpperCase(),
          name: name.trim(),
          color,
          github_repo: githubRepo.trim() || null,
          default_branch: defaultBranch.trim() || "main",
          test_command: testCommand.trim() || null,
          merge_policy: mergePolicy,
        })
      ).data;

      // 2) Upload the markdown context docs (any subset provided).
      if (brd || architecture || codeHelper) {
        const form = new FormData();
        if (brd) form.append("brd", brd, brd.name);
        if (architecture) form.append("architecture", architecture, architecture.name);
        if (codeHelper) form.append("code_helper", codeHelper, codeHelper.name);
        await api.post<ProjectDocStatus>(`/superadmin/projects/${project.id}/docs`, form);
      }

      // 3) Import the tasks spreadsheet into the backlog.
      let imported: TasksImportResult | null = null;
      if (tasksFile) {
        const form = new FormData();
        form.append("file", tasksFile, tasksFile.name);
        imported = (
          await api.post<TasksImportResult>(
            `/superadmin/projects/${project.id}/tasks-import`,
            form,
          )
        ).data;
      }
      return { project, imported };
    },
    onSuccess: ({ project, imported }) => {
      const parts = [`Created ${project.key} — ${project.name}.`];
      if (imported) {
        const stored = imported.deferred || imported.created;
        parts.push(
          `Stored ${stored} task${stored === 1 ? "" : "s"} — they become tickets when you assign the project to a tenant.`,
        );
      }
      setOkMsg(parts.join(" "));
      setError(null);
      setKey("");
      setName("");
      setGithubRepo("");
      setTestCommand("");
      setBrd(null);
      setArchitecture(null);
      setCodeHelper(null);
      setTasksFile(null);
      qc.invalidateQueries({ queryKey: ["superadmin-projects"] });
      qc.invalidateQueries({ queryKey: ["superadmin-projects-progress"] });
    },
    onError: (err) => {
      setError(extractErrorMessage(err, "Could not create the project."));
      setOkMsg(null);
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!key.trim() || !name.trim() || create.isPending) return;
    create.mutate();
  };

  // Group projects by tenant for the list; tenant-less ones sit under "Unassigned".
  const byTenant = new Map<string, SuperProject[]>();
  for (const p of projects) {
    const k = p.tenant_name ?? "Unassigned";
    byTenant.set(k, [...(byTenant.get(k) ?? []), p]);
  }

  return (
    <div style={{ maxWidth: 880 }}>
      <header style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px", margin: 0 }}>
          Create project
        </h1>
        <p style={{ color: "var(--mute)", fontSize: 13, marginTop: 6 }}>
          Create a project: set its repo + coding config and upload the BRD,
          architecture, code-helper, and tasks spreadsheet. No tenant here — you
          assign it to a tenant and a team on the Assign Project tab, which is
          when its backlog tickets are created.
        </p>
      </header>

      <section style={card}>
        <form onSubmit={onSubmit} style={{ display: "grid", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "160px 1fr 120px", gap: 14 }}>
            <label style={field}>
              <span style={label}>Key</span>
              <input
                type="text"
                value={key}
                onChange={(e) => setKey(e.target.value.toUpperCase())}
                placeholder="APOLLO"
                maxLength={16}
                style={{ ...input, fontFamily: "var(--font-mono)" }}
                autoComplete="off"
                required
              />
            </label>
            <label style={field}>
              <span style={label}>Name</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Apollo platform"
                maxLength={255}
                style={input}
                required
              />
            </label>
            <label style={field}>
              <span style={label}>Color</span>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                style={{ ...input, padding: 4, height: 38, cursor: "pointer" }}
              />
            </label>
          </div>

          <div style={divider}>Coding config — Atlas agent</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 160px", gap: 14 }}>
            <label style={field}>
              <span style={label}>GitHub repo</span>
              <input
                type="text"
                value={githubRepo}
                onChange={(e) => setGithubRepo(e.target.value)}
                placeholder="org/repo"
                maxLength={255}
                style={{ ...input, fontFamily: "var(--font-mono)" }}
                autoComplete="off"
              />
            </label>
            <label style={field}>
              <span style={label}>Default branch</span>
              <input
                type="text"
                value={defaultBranch}
                onChange={(e) => setDefaultBranch(e.target.value)}
                placeholder="main"
                maxLength={64}
                style={{ ...input, fontFamily: "var(--font-mono)" }}
                autoComplete="off"
              />
            </label>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <label style={field}>
              <span style={label}>Test command (optional)</span>
              <input
                type="text"
                value={testCommand}
                onChange={(e) => setTestCommand(e.target.value)}
                placeholder="pytest -q"
                maxLength={512}
                style={{ ...input, fontFamily: "var(--font-mono)" }}
                autoComplete="off"
              />
            </label>
            <label style={field}>
              <span style={label}>Merge policy</span>
              <select value={mergePolicy} onChange={(e) => setMergePolicy(e.target.value)} style={input}>
                {MERGE_POLICIES.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div style={divider}>Onboarding documents</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <FileField label="BRD.md" accept=".md,text/markdown" file={brd} onPick={setBrd} />
            <FileField label="architecture.md" accept=".md,text/markdown" file={architecture} onPick={setArchitecture} />
            <FileField label="code-helper.md" accept=".md,text/markdown" file={codeHelper} onPick={setCodeHelper} />
            <FileField
              label="tasks.xlsx"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              file={tasksFile}
              onPick={setTasksFile}
              hint="serial_id + description"
            />
          </div>

          {error && <div role="alert" style={errBar}>{error}</div>}
          {okMsg && <div role="status" style={okBar}>{okMsg}</div>}

          <div>
            <button type="submit" disabled={create.isPending} style={primaryBtn(create.isPending)}>
              {create.isPending ? "Creating…" : "Create project"}
            </button>
          </div>
        </form>
      </section>

      <section style={{ marginTop: 28 }}>
        <div style={sectionTitle}>All projects · {projects.length}</div>
        {projectsQuery.isLoading ? (
          <p style={{ color: "var(--mute)" }}>Loading…</p>
        ) : projects.length === 0 ? (
          <p style={{ color: "var(--mute)" }}>No projects yet.</p>
        ) : (
          [...byTenant.entries()].map(([tName, ps]) => (
            <div key={tName} style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--body-mid)", marginBottom: 6 }}>
                {tName}
              </div>
              <div style={{ border: "1px solid var(--hairline)", borderRadius: 8, overflow: "hidden" }}>
                {ps.map((p) => (
                  <div key={p.id} style={projRow}>
                    <span aria-hidden style={{ width: 10, height: 10, borderRadius: 3, background: p.color }} />
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--mute)", width: 90 }}>{p.key}</span>
                    <span style={{ flex: 1, fontSize: 13.5 }}>{p.name}</span>
                    {p.github_repo && (
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--mute)" }}>
                        {p.github_repo}
                      </span>
                    )}
                    <span style={{ fontSize: 12, color: "var(--mute)" }}>
                      {p.member_count} member{p.member_count === 1 ? "" : "s"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </section>
    </div>
  );
}

function FileField({
  label: text,
  accept,
  file,
  onPick,
  hint,
}: {
  label: string;
  accept: string;
  file: File | null;
  onPick: (f: File | null) => void;
  hint?: string;
}) {
  return (
    <label style={field}>
      <span style={label}>
        {text}
        {hint && <span style={{ color: "var(--mute)", fontWeight: 400 }}> · {hint}</span>}
      </span>
      <input
        type="file"
        accept={accept}
        onChange={(e) => onPick(e.target.files?.[0] ?? null)}
        style={{ ...input, padding: "7px 9px", cursor: "pointer" }}
      />
      {file && <span style={{ fontSize: 11.5, color: "var(--mute)" }}>{file.name}</span>}
    </label>
  );
}

const card: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: 20,
};
const field: CSSProperties = { display: "flex", flexDirection: "column", gap: 6 };
const label: CSSProperties = { fontSize: 12.5, fontWeight: 500, color: "var(--body-mid)" };
const divider: CSSProperties = {
  fontSize: 11, fontWeight: 500, letterSpacing: 1, textTransform: "uppercase",
  color: "var(--mute)", marginTop: 6, paddingTop: 12, borderTop: "1px solid var(--hairline)",
};
const input: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 6,
  padding: "9px 11px",
  fontSize: 14,
  color: "var(--ink)",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};
function primaryBtn(disabled: boolean): CSSProperties {
  return {
    background: "var(--ink)", color: "var(--on-primary)", border: "none", borderRadius: 4,
    padding: "10px 18px", fontSize: 14, fontWeight: 550,
    cursor: disabled ? "wait" : "pointer", opacity: disabled ? 0.6 : 1,
  };
}
const sectionTitle: CSSProperties = {
  fontSize: 11, fontWeight: 500, letterSpacing: 1.2, textTransform: "uppercase",
  color: "var(--mute)", marginBottom: 10,
};
const projRow: CSSProperties = {
  display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
  borderBottom: "1px solid var(--hairline)", background: "var(--canvas)",
};
const errBar: CSSProperties = {
  padding: "9px 12px", fontSize: 13, color: "#ee1d36",
  background: "rgba(238,29,54,0.06)", border: "1px solid rgba(238,29,54,0.3)", borderRadius: 6,
};
const okBar: CSSProperties = {
  padding: "9px 12px", fontSize: 13, color: "#047a18",
  background: "rgba(0,215,34,0.06)", border: "1px solid rgba(0,215,34,0.3)", borderRadius: 6,
};
